import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import { notify } from '../utils/notifications';
import { Lock, CheckCircle, AlertCircle, ArrowRight, ArrowLeft, Key } from 'lucide-react';
import './Login.css';

const PrimoAccesso = () => {
  const [step, setStep] = useState(1);
  const { utente, updateUser } = useAuthStore();

  const [nuovaPassword, setNuovaPassword] = useState('');
  const [confermaPassword, setConfermaPassword] = useState('');
  const [caricamento, setCaricamento] = useState(false);
  const navigate = useNavigate();

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione
    if (nuovaPassword !== confermaPassword) {
      notify.error('Le password non corrispondono');
      return;
    }

    if (nuovaPassword.length < 8) {
      notify.error('La password deve essere almeno 8 caratteri');
      return;
    }

    const toastId = notify.loading('Configurazione profilo...');
    setCaricamento(true);

    try {
      // L'email è già quella con cui il cliente ha appena fatto accesso:
      // al primo accesso resta solo da scegliere una password personale.
      await api.post(`/autenticazione/utenti/${utente.id}/cambia_password/`, {
        vecchia_password: utente.titolare.password_temporanea,
        nuova_password: nuovaPassword
      });

      await api.patch(`/anagrafiche/titolari/${utente.titolare.id}/`, {
        primo_accesso_completato: true
      });

      updateUser({
        ...utente,
        titolare: {
          ...utente.titolare,
          primo_accesso_completato: true
        }
      });

      notify.update(toastId, { 
        type: 'success', 
        render: 'Profilo configurato! Benvenuto in MyCasella 🎉' 
      });

      // Reindirizza alla dashboard
      setTimeout(() => {
        navigate('/utente/dashboard');
      }, 1500);

    } catch (error) {
      console.error('Errore configurazione profilo:', error);
      const dati = error.response?.data || {};
      const primoErrore = Object.values(dati).flat().find(v => typeof v === 'string');
      const errorMsg = dati.detail || primoErrore || error.message ||
                       'Errore nella configurazione del profilo';
      notify.update(toastId, { type: 'error', render: errorMsg });
    } finally {
      setCaricamento(false);
    }
  };

  const renderStep1 = () => (
    <div className="step-content">
      <div className="welcome-icon">
        👋
      </div>
      
      <h2 className="step-title">
        Benvenuto in MyCasella!
      </h2>
      
      <p className="step-description">
        Ciao <strong>{utente?.first_name || 'utente'}</strong>, siamo felici di averti con noi!<br/>
        È il tuo primo accesso, quindi abbiamo bisogno di configurare insieme il tuo account.
      </p>

      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-semibold mb-1">
              Importante da sapere:
            </p>
            <p className="text-sm text-amber-700">
              Per accedere userai la tua email <strong>{utente?.titolare?.email || utente?.email}</strong>{' '}
              e la password che stai per scegliere. La password temporanea che ti abbiamo
              inviato non sarà più valida.
            </p>
          </div>
        </div>
      </div>

      <button 
        onClick={handleNext}
        className="login-button w-full"
        style={{ marginTop: '16px' }}
      >
        Iniziamo <ArrowRight className="inline-block ml-2" size={20} />
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <div className="welcome-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
        <Key className="text-white" size={40} />
      </div>
      
      <h2 className="step-title">
        Crea la tua Password
      </h2>
      
      <p className="step-description">
        Scegli una password sicura (almeno 8 caratteri)
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="form-group">
          <label htmlFor="nuova_password" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Nuova Password *
          </label>
          <input
            id="nuova_password"
            type="password"
            value={nuovaPassword}
            onChange={(e) => setNuovaPassword(e.target.value)}
            required
            disabled={caricamento}
            autoFocus
            placeholder="Minimo 8 caratteri"
            minLength={8}
            className="w-full"
          />
        </div>

        <div className="form-group">
          <label htmlFor="conferma_password" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Conferma Password *
          </label>
          <input
            id="conferma_password"
            type="password"
            value={confermaPassword}
            onChange={(e) => setConfermaPassword(e.target.value)}
            required
            disabled={caricamento}
            placeholder="Ripeti la password"
            minLength={8}
            className="w-full"
          />
          {nuovaPassword && confermaPassword && nuovaPassword !== confermaPassword && (
            <p className="text-xs text-red-600 mt-1 font-semibold">
              ⚠️ Le password non corrispondono
            </p>
          )}
          {nuovaPassword && confermaPassword && nuovaPassword === confermaPassword && (
            <p className="text-xs text-green-600 mt-1 font-semibold">
              ✓ Le password corrispondono
            </p>
          )}
        </div>

        <div className="button-group">
          <button 
            type="button"
            onClick={handleBack}
            className="button-secondary"
            disabled={caricamento}
          >
            <ArrowLeft className="inline-block mr-2" size={18} />
            Indietro
          </button>
          <button 
            type="submit" 
            disabled={caricamento || !nuovaPassword || !confermaPassword || nuovaPassword !== confermaPassword} 
            className="login-button button-primary"
          >
            {caricamento ? 'Configurazione...' : (
              <>
                Completa <CheckCircle className="inline-block ml-2" size={18} />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="login-container">
      <div className="login-box" style={{ maxWidth: '480px' }}>
        <div className="login-header">
          <img src="/logo.png" alt="Casella Logo" className="login-logo" />
        </div>

        {/* Indicatore Step */}
        <div className="step-indicator">
          <div className={`step-dot ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}></div>
          <div className={`step-dot ${step >= 2 ? 'active' : ''}`}></div>
        </div>

        {/* Contenuto Step */}
        <div className="step-container">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
        </div>
      </div>
      
      <div className="powered-by-footer">
        Powered by <a href="https://neoflux.it" target="_blank" rel="noopener noreferrer"><span>NEOFLUX</span></a>
      </div>
    </div>
  );
};

export default PrimoAccesso;
